import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../../common/types';
import { useSMLRouter } from '../../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../../routing/sml/SMLRoutes';
import { platformName } from '../../../platformMeta';
import type { SerializedSocialChannel, SocialPlatform } from '../../../types';
import { channelsByPostingMode } from '../rollout/launch/postingModes';
import { rolloutMessages } from '../rollout/rolloutMessages';
/* eslint-disable css-modules/no-unused-class -- each part uses its own slice of the two stylesheets */
import rolloutStyles from '../rollout/Rollout.module.scss';
import styles from './ReelsPlan.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReelsPlanPostingModesProps {
  /** The channel rows of the artist. */
  channels: SerializedSocialChannel[];
  /** The channels of the campaign. */
  selected: SocialPlatform[];
}

/**
 * Under the channels of the Auto campaign (QA finding B14): which channels post by themselves and
 * which come as a "post it yourself" reminder at their time, with the words of the Release plan.
 * With no connected channel it says so and offers "Connect a channel".
 */
const ReelsPlanPostingModesComponent: React.FC<ReelsPlanPostingModesProps> = ({ channels, selected }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const openChannels = useCallback(() => router.push(SMLRoutes.promoteChannels), [router]);
  const modes = useMemo(
    () => channelsByPostingMode(channels.filter((channel) => selected.includes(channel.platform))),
    [channels, selected]
  );
  if (modes.auto.length === 0 && modes.manual.length === 0) {
    return null;
  }
  const names = (list: SerializedSocialChannel[]) =>
    intl.formatList(list.map((channel) => platformName(channel.platform)));

  return (
    <p className={styles.setHint} data-testid="reels-plan-posting-modes">
      {modes.auto.length > 0 && (
        <span data-testid="reels-plan-posting-auto">
          <b>{intl.formatMessage(rolloutMessages.channelsAuto)}</b> {names(modes.auto)}.{' '}
        </span>
      )}
      {modes.manual.length > 0 && (
        <span data-testid="reels-plan-posting-manual">
          <b>{intl.formatMessage(rolloutMessages.channelsManual)}</b> {names(modes.manual)}.{' '}
        </span>
      )}
      {modes.auto.length === 0 && (
        <>
          {intl.formatMessage(rolloutMessages.channelsNoneConnected)}{' '}
          <button type="button" className={rolloutStyles.link} onClick={openChannels}>
            {intl.formatMessage(rolloutMessages.connectChannel)}
          </button>
        </>
      )}
    </p>
  );
};

export const ReelsPlanPostingModes = TypedMemo(ReelsPlanPostingModesComponent);
